import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { BoardDto, BoardResource, CardDto, ColumnDto, CreateBoardDto, T_EmailType } from "dtos/kanban.dto";
import { FilterType } from "components/Kanban/BoardSectionList";
import { useDebounce } from "hooks/use-debounce";
import { T_MongoDoc, T_MongoId } from "types/_utils.types";
import { ApplicationDto } from "dtos/application.dto";
import { CreditorDto } from "dtos/creditor.dto";
import { useState } from "react";
import { LeadDto } from "dtos/lead.dto";
import { ActivityLog } from "components/Pipeline/PipelineActivity";

export const kanbanService = {
  useGetBoardData: (boardId: string | undefined) => {
    return useQuery({
      queryKey: ["get-columns", boardId],
      queryFn: async () => {
        const res = await api.get(`/board/${boardId}/columns`);
        return res.data as T_Api_Success_Res<BoardDto>;
      },
      enabled: !!boardId && boardId.length > 0,
      refetchInterval: (query) => {
        return query.state.data?.result?.isSyncing ? 5000 : false;
      },
    });
  },
  useCopyColumnsConfig: () => {
    return useMutation({
      mutationFn: async (boardId: T_MongoId) => {
        const { data } = await api.get<T_Api_Success_Res<BoardDto>>(`/board/${boardId}/columns`);
        const { columns, groups } = data.result;

        const sortedGroups = (groups ?? []).slice().sort((a, b) => a.position - b.position);
        const sortedColumns = columns.slice().sort((a, b) => a.position - b.position);

        const groupMap = Object.fromEntries(
          sortedGroups.map((g) => [
            g.name,
            {
              name: g.name,
              position: g.position,
              meta: g.meta ?? {},
              columns: [] as ColumnDto[],
            },
          ]),
        );

        const ungroupedColumns: ColumnDto[] = [];

        sortedColumns.forEach((col) => {
          const entry: ColumnDto = {
            id: col.id,
            name: col.name,
            position: col.position,
            group: col.group,
            disabled: col.disabled,
            meta: col.meta ?? {},
          };

          if (col.group && groupMap[col.group]) {
            groupMap[col.group].columns.push(entry);
          } else {
            ungroupedColumns.push(entry);
          }
        });

        const serialized = {
          groups: sortedGroups.map((g) => groupMap[g.name]),
          ungroupedColumns,
          columnOrder: sortedColumns.map((c) => c.id),
          groupOrder: sortedGroups.map((g) => g.name),
        };

        await navigator.clipboard.writeText(JSON.stringify(serialized, null, 2));
      },
    });
  },
  useGetBoard: (params: { resource?: BoardResource; orgCode?: string; scope?: string }) => {
    return useQuery({
      queryKey: ["get-board", params],
      queryFn: async () => {
        const res = await api.get<T_Api_Success_Res<BoardDto[]>>("/board", {
          params,
        });

        return res.data.result.at(0);
      },
    });
  },
  useGetCard: ({
    boardId,
    column_id,
    start_date,
    end_date,
    type,
    search,
    scope,
    checkReady = () => true,
  }: {
    boardId: string | undefined;
    column_id?: (string | null)[];
    start_date?: string;
    end_date?: string;
    type?: FilterType;
    search?: string;
    scope?: Record<string, string>;
    checkReady?: () => boolean;
  }) => {
    const debouncedSearch = useDebounce(search, 500);
    const debouncedType = useDebounce(type, 2000);
    const debouncedStartDate = useDebounce(start_date, 2000);
    const debouncedEndDate = useDebounce(end_date, 2000);
    const debouncedColumnIds = useDebounce(column_id, 2000);

    return useQuery({
      queryKey: [
        "Card",
        boardId,
        column_id ?? [],
        debouncedColumnIds,
        debouncedType,
        debouncedStartDate,
        debouncedEndDate,
        debouncedSearch,
        scope,
      ],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<{ columnId: string; cards: CardDto[] }[]>>(
          `/card/board/${boardId}`,
          {
            params: {
              scope,
              ...(debouncedType && debouncedStartDate && debouncedEndDate
                ? {
                    column_id: debouncedColumnIds,
                    type: debouncedType,
                    start_date: debouncedStartDate,
                    end_date: debouncedEndDate,
                  }
                : {}),
              ...(debouncedSearch ? { search: debouncedSearch } : {}),
            },
          },
        );

        return data;
      },
      enabled: checkReady,
    });
  },
  useSyncCards: () => {
    const queryClient = useQueryClient();
    return useMutation({
      async mutationFn({ boardId, mode = "async" }: { boardId: string; mode?: "sync" | "async" }) {
        const { data } = await api.post(`/card/board/${boardId}/sync-cards`, {}, { params: { mode } });
        return data;
      },
      onSuccess(_, { boardId }) {
        queryClient.setQueryData<T_Api_Success_Res<BoardDto>>(["get-columns", boardId], (old) => {
          if (!old) return old;
          return {
            ...old,
            result: {
              ...old.result,
              isSyncing: true,
            },
          };
        });
      },
    });
  },
  useMoveCard: (boardId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["moveCard"],
      mutationFn: async ({ cardId, to_column_id }: { cardId: string; to_column_id: string }) => {
        const { data } = await api.patch<T_Api_Success_Res<CardDto>>(`/card/${cardId}/move`, { to_column_id });
        return data;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["Card", boardId] });
      },
    });
  },
  useMoveCards: (boardId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["moveCards"],
      mutationFn: async (updates: { card_id: string; column_id: string }[]) => {
        const { data } = await api.patch<T_Api_Success_Res<{ moved: number }>>(`/card/move/bulk`, {
          board_id: boardId,
          updates,
        });

        return data.result;
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["Card", boardId] });
      },
    });
  },
  useCreateBoard() {
    return useMutation({
      async mutationFn({ orgCode, appKey, ...payload }: CreateBoardDto) {
        const { data } = await api.post<T_Api_Success_Res<T_MongoDoc & BoardDto>>("/board", payload, {
          params: { orgCode, appKey },
        });

        return data;
      },
    });
  },
  useEditColumns() {
    return useMutation({
      mutationFn: async ({ _id, columns, groups }: Pick<BoardDto, "_id" | "columns" | "groups">) => {
        await api.patch(`/board/${_id}/columns`, { columns, groups }, { params: { noEmptyGroups: false } });
      },
    });
  },
  useCreateColumns() {
    const qc = useQueryClient();
    return useMutation({
      async mutationFn({
        board_id,
        ...payload
      }: {
        board_id: T_MongoId | undefined;
        columns: Pick<ColumnDto, "name" | "group" | "meta">[];
      }) {
        if (!board_id) return;
        const { data } = await api.post<T_Api_Success_Res<T_MongoDoc>>(`/board/${board_id}/columns/bulk`, payload);
        return data;
      },
      onSuccess() {
        qc.invalidateQueries({ queryKey: ["get-boards"] });
      },
    });
  },
  useGetBoards(orgCode: string | undefined) {
    return useQuery({
      queryKey: ["get-boards", orgCode],
      async queryFn() {
        const { data } = await api.get<T_Api_Success_Res<BoardDto[]>>("/board", { params: { orgCode } });
        return data.result;
      },
      enabled: Boolean(orgCode?.length),
    });
  },
  useDeleteBoard(params: { delete_columns: boolean }) {
    return useMutation({
      async mutationFn(boardId: string) {
        const { data } = await api.delete(`/board/${boardId}`, { params });
        return data;
      },
    });
  },
  useGetScopeByItems(
    board: Pick<BoardDto, "_id" | "scope"> | undefined,
    params: { page: number; limit: number; search: string },
    checkReady?: () => boolean,
  ) {
    return useQuery({
      queryKey: ["board-scope", board?._id, params],
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<object>>>(
          `/board/${board?._id}/scoped-items`,
          { params },
        );

        const typeCast = {
          toApplication(item: unknown): item is ApplicationDto {
            return board?.scope === BoardResource.APPLICATION;
          },
          toCreditor(item: unknown): item is CreditorDto {
            return board?.scope === BoardResource.CREDITOR;
          },
        };

        data.result.data = data.result.data.map((item) => {
          if (typeCast.toApplication(item)) {
            return {
              title: item.meta.client_name,
              value: item._id,
            };
          } else if (typeCast.toCreditor(item)) {
            return {
              title: item.name,
              value: item._id,
            };
          } else {
            return {
              title: (item as { _id?: string })?._id,
              value: (item as { _id?: string })?._id,
            };
          }
        });

        return data.result as T_Api_Paginated<{ title: string; value?: string }>;
      },
      enabled: checkReady,
    });
  },
  useGetScopeByItemsInfinite(
    board: Pick<BoardDto, "_id" | "scope"> | undefined,
    params: Partial<{ defaultPage: number; defaultLimit: number; defaultSearch: string; scopeId: T_MongoId }>,
    checkReady?: () => boolean,
  ) {
    const [limit, setLimit] = useState(params.defaultLimit ?? 10);
    const [search, setSearch] = useState(params.defaultSearch ?? "");
    const debouncedSearch = useDebounce(search, 500);

    const query = useInfiniteQuery({
      queryKey: ["board-scope", board?._id, debouncedSearch, limit, params.scopeId],
      initialPageParam: params.defaultPage ?? 1,
      staleTime: 5 * 60 * 1000,

      queryFn: async ({ pageParam }) => {
        const { data } = await api.get<T_Api_Success_Res<T_Api_Paginated<object>>>(
          `/board/${board?._id}/scoped-items`,
          {
            params: {
              page: pageParam,
              limit: limit,
              search: debouncedSearch,
              scopeId: params.scopeId,
            },
          },
        );

        const typeCast = {
          toApplication(item: unknown): item is ApplicationDto {
            return board?.scope === BoardResource.APPLICATION;
          },

          toCreditor(item: unknown): item is CreditorDto {
            return board?.scope === BoardResource.CREDITOR;
          },
        };

        data.result.data = data.result.data.map((item) => {
          if (typeCast.toApplication(item)) {
            const clientTitle = item.meta.client_name || item.meta.business_name || item.meta.email;
            const affiliateTitle = item.affiliate?.name || item.affiliate?.email;
            const typeCastedLead = item.lead as LeadDto | string | undefined;
            const leadTitle =
              !typeCastedLead || typeof typeCastedLead === "string" ? "" : typeCastedLead.name || typeCastedLead.email;

            return {
              title: clientTitle || leadTitle || affiliateTitle,
              value: item._id,
            };
          }

          if (typeCast.toCreditor(item)) {
            return {
              title: item.name,
              value: item._id,
            };
          }

          return {
            title: (item as { _id?: string })?._id,
            value: (item as { _id?: string })?._id,
          };
        });

        return data.result as T_Api_Paginated<{
          title: string;
          value?: string;
        }>;
      },

      getNextPageParam: (lastPage) => {
        return lastPage.hasNextPage ? lastPage.nextPage : undefined;
      },

      getPreviousPageParam: (firstPage) => {
        return firstPage.hasPrevPage ? firstPage.prevPage : undefined;
      },

      enabled: checkReady?.(),
    });

    return {
      query,
      params: { limit, search },
      setLimit,
      setSearch,
    };
  },
  useUndoCard: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: ["cards", "undo"],
      mutationFn: async ({ card_id }: { card_id: string }) => {
        const res = await api.post(`/card/${card_id}/undo`);

        return res.data as T_Api_Success_Res<{
          card: {
            _id: string;
            board_id: string;
            column_id: string;
            reference_id: string;
          };
          isBackward: boolean;
          emailAlreadySent: boolean;
          regularTemplate?: {
            subject: string;
            body: string;
          };
          systemFaultTemplate?: {
            subject: string;
            body: string;
          };
        }>;
      },
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: ["Card"] });
      },
    });
  },
  useConfirmUndo: () => {
    return useMutation({
      mutationKey: ["cards", "undo", "confirm"],
      mutationFn: async ({ card_id, emailType }: { card_id: string; emailType: T_EmailType }) => {
        const res = await api.post(`/cards/${card_id}/undo/confirm`, {
          emailType,
        });

        return res.data;
      },
    });
  },
  useGetBoardActivity: ({ boardId, page, limit }: { boardId: string | undefined; page: number; limit: number }) => {
    return useQuery({
      queryKey: ["get-board-activity", boardId, page, limit],
      queryFn: async () => {
        const res = await api.get(`/board/${boardId}/activity`, {
          params: { page, limit },
        });
        return res.data as T_Api_Success_Res<T_Api_Paginated<ActivityLog>>;
      },
      enabled: !!boardId,
    });
  },
};
